import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { ORG_ROLES, strings } from '@constants'
import { getAllCommodityApi, getAllEmployees } from '@data'
import { showLoader } from '@data/state/action'
import { DrawerBodyWrapper, DrawerFooterWrapper } from '@base'
import { Button, Input, MultiSelect, PaymentTermsCard, Select } from '@baseV2'

import { InputsWrapper, PaymentTermsWrapper } from '../styles'
import { User } from '@data/model'

const ROLE_OPTIONS = Object.values(ORG_ROLES)

// the commodity whose subsidiaries are tied to a project instead of a trade name
const PROJECT_COMMODITY_ID = 'CO4dR3o-7996'

// Builds the form state. `data` is null when adding; when editing it carries the subsidiary's values
// ({ role, commodity, relationship_manager, trade_name, project_code, project_sites }).
const deriveFormState = (data = null) => ({
	role: {
		label: strings('role'),
		placeholder: strings('select', 'role'),
		disabled: false,
		value: ROLE_OPTIONS.find((item) => item.name === data?.role?.name) ?? '',
		required: true,
	},
	commodity: {
		label: strings('commodity'),
		placeholder: strings('select', 'commodity'),
		disabled: false,
		value: data?.commodity ?? '',
		required: true,
	},
	relationship_manager: {
		label: 'Relationship Manager',
		placeholder: 'Select relationship manager',
		disabled: false,
		value: data?.relationship_manager ?? '',
		required: false,
	},
	trade_name: {
		label: strings('trade_name'),
		placeholder: strings('select', 'trade_name'),
		disabled: false,
		value: data?.trade_name ?? '',
		required: false,
	},
	project_code: {
		label: 'Project Code',
		placeholder: 'Enter project code',
		disabled: false,
		value: data?.project_code ?? '',
		required: true,
	},
	project_sites: {
		label: 'Project Sites',
		placeholder: 'Select or create project sites',
		disabled: false,
		value: data?.project_sites ?? [],
		required: false,
	},
})

// which fields are shown for the current role / commodity — nothing but the role until one is picked
const getVisibleKeys = (formState) => {
	const role = formState.role.value?.name
	if (!role) return []
	if (role === ORG_ROLES.marketplaceBuyer.name) {
		const base = ['commodity', 'relationship_manager']
		return formState.commodity.value?.commodity_id === PROJECT_COMMODITY_ID ? [...base, 'project_code', 'project_sites'] : [...base, 'trade_name']
	}
	if (role === ORG_ROLES.marketplaceSupplier.name || role === ORG_ROLES.logisticsSupplier.name) return ['trade_name', 'relationship_manager']
	return []
}

// used for both create and edit: pass `data` when editing
const AddSubsidiary = ({ isEdit = false, data = null, orgData, paymentTerms = [], oracleId, onSubmit }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(() => deriveFormState(data))
	const [commodityList, setCommodityList] = useState([])
	const [employeeList, setEmployeeList] = useState([])

	useEffect(() => {
		dispatch(showLoader(true))
		Promise.all([
			getAllCommodityApi()
				.then((res) => setCommodityList(res ?? []))
				.catch((err) => console.log(err)),
			getAllEmployees()
				.then((res) => setEmployeeList((res ?? []).map((e) => ({ ...new User(e).getUserDetails() }))))
				.catch((err) => console.log(err)),
		]).finally(() => dispatch(showLoader(false)))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// the org's distinct trade names, matched case insensitively — same as PushToOracle
	const tradeNames = useMemo(() => {
		const names = new Map()
		;(orgData?.gst_details ?? []).forEach((item) => {
			if (!item?.gst || !item?.trade_name) return
			const key = item.trade_name.trim().toLowerCase()
			if (!names.has(key)) names.set(key, { id: key, name: item.trade_name })
		})
		return [...names.values()]
	}, [orgData?.gst_details])

	const setField = (key, value) => setFormState((state) => ({ ...state, [key]: { ...state[key], value } }))

	// changing the role or commodity changes which fields apply, so the dependent ones start afresh
	const resetFrom = (keys) =>
		setFormState((state) => {
			const fresh = deriveFormState(null)
			return keys.reduce((acc, key) => ({ ...acc, [key]: fresh[key] }), state)
		})

	const roleHandler = (value) => {
		resetFrom(['commodity', 'relationship_manager', 'trade_name', 'project_code', 'project_sites'])
		setField('role', value)
	}

	const commodityHandler = (value) => {
		resetFrom(['trade_name', 'project_code', 'project_sites'])
		setField('commodity', value)
	}

	const visibleKeys = getVisibleKeys(formState)

	const fieldProps = (key) => ({
		id: `subsidiary-${key}`,
		label: `${formState[key].label}${formState[key].required ? ' *' : ''}`,
		placeholder: formState[key].placeholder,
		disabled: formState[key].disabled,
		value: formState[key].value,
	})

	const renderers = {
		commodity: () => (
			<Select
				{...fieldProps('commodity')}
				disabled={formState.commodity.disabled || isEdit}
				data={commodityList}
				displayKey='commodity_name'
				primaryKey='commodity_id'
				onChange={commodityHandler}
			/>
		),
		relationship_manager: () => (
			<Select
				{...fieldProps('relationship_manager')}
				data={employeeList}
				displayKey='full_name'
				primaryKey='user_id'
				onChange={(v) => setField('relationship_manager', v)}
			/>
		),
		trade_name: () => (
			<Select
				{...fieldProps('trade_name')}
				data={tradeNames}
				displayKey='name'
				primaryKey='id'
				onChange={(v) => setField('trade_name', v)}
				// with a lone trade name there is nothing to choose between
				disabled={formState.trade_name.disabled || tradeNames.length === 1}
			/>
		),
		project_code: () => <Input {...fieldProps('project_code')} onChange={(e) => setField('project_code', e.target.value)} />,
		project_sites: () => (
			<MultiSelect
				{...fieldProps('project_sites')}
				data={[]}
				displayKey='title'
				primaryKey='title'
				onChange={(v) => setField('project_sites', v)}
				createNewOption
			/>
		),
	}

	// with a lone trade name there is nothing to choose, so it is preselected
	useEffect(() => {
		if (tradeNames.length === 1 && visibleKeys.includes('trade_name') && !formState.trade_name.value?.id) setField('trade_name', tradeNames[0])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tradeNames, visibleKeys.includes('trade_name')])

	// every visible required field must be filled
	const disabled =
		!visibleKeys.length ||
		!['role', ...visibleKeys].every((key) => {
			const { required, value } = formState[key]
			if (!required) return true
			return Array.isArray(value) ? value.length > 0 : typeof value === 'string' ? !!value.trim() : !!Object.keys(value ?? {}).length
		})

	const submitHandler = () => {
		if (disabled) return
		const values = Object.fromEntries(['role', ...visibleKeys].map((key) => [key, formState[key].value]))
		onSubmit?.(values)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					{/* the role of an existing subsidiary cannot be changed */}
					<Select
						{...fieldProps('role')}
						data={ROLE_OPTIONS}
						displayKey='name'
						primaryKey='name'
						onChange={roleHandler}
						disabled={formState.role.disabled || isEdit}
					/>
					{visibleKeys.map((key) => (
						<React.Fragment key={key}>{renderers[key]()}</React.Fragment>
					))}
				</InputsWrapper>
				{/* read-only: the terms are changed from the Actions menu */}
				{isEdit && (
					<PaymentTermsWrapper>
						<PaymentTermsCard paymentTermsList={paymentTerms} showAction={false} emptyText='No payment terms added' />
					</PaymentTermsWrapper>
				)}
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button color='primary' disabled={disabled} onClick={submitHandler}>
					{isEdit ? 'Update Subsidiary' : 'Add Subsidiary'}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddSubsidiary
